import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../api";
import {
  AREA_BUCKETS,
  dayKey,
  pct,
  resourceProgress,
  subjectPalette,
  sumResourceCounts,
} from "../Student/utils/progressFormat";

const CARD_SX = {
  p: 2,
  height: "100%",
  borderRadius: 3,
  boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  border: "1px solid #eef0f4",
};

const ACTIVITY_LIMIT = 60;
const ATTEMPTS_LIMIT = 50;

const formatDate = (value) =>
  value ? new Date(value).toLocaleString('en-GB', { dateStyle: "medium", timeStyle: "short" }) : "—";

const ProgressRow = ({ label, value, right, color, status, statusColor }) => (
  <Box sx={{ mb: 1.25, minWidth: 0 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, gap: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#374151" }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
        {status ? (
          <Chip
            size="small"
            label={status}
            sx={{
              height: 18,
              fontSize: "0.6rem",
              fontWeight: 800,
              bgcolor: `${statusColor || "#95a5a6"}18`,
              color: statusColor || "#95a5a6",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ) : null}
        <Typography variant="caption" sx={{ fontWeight: 800, color: color || "#111827" }}>{right}</Typography>
      </Box>
    </Box>
    <LinearProgress
      variant="determinate"
      value={pct(value)}
      sx={{
        height: 7,
        borderRadius: 5,
        bgcolor: "#f1f2f6",
        "& .MuiLinearProgress-bar": { borderRadius: 5, bgcolor: color || "#7c3aed" },
      }}
    />
  </Box>
);

export default function MobileUserProgress() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [subjectCounts, setSubjectCounts] = useState({});
  const [areasBySubject, setAreasBySubject] = useState({});
  const [activity, setActivity] = useState([]);
  const [attempts, setAttempts] = useState([]);

  const [examBoardId, setExamBoardId] = useState(null);
  const [areaSubjectId, setAreaSubjectId] = useState(null);
  const [areaBucket, setAreaBucket] = useState("weak_areas");
  const [trendSubjectId, setTrendSubjectId] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("week");

  const base = `admin/mobile-users/${id}/progress`;

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get(`${base}/activity`, { params: { limit: ACTIVITY_LIMIT } }),
      api.get("admin/ai-tests/attempts", { params: { student_id: id, limit: ATTEMPTS_LIMIT, include_test: 1 } }),
    ]).then(([act, att]) => {
      if (cancelled) return;
      if (act.status === "fulfilled") setActivity(act.value.data?.data?.activity ?? []);
      if (att.status === "fulfilled") setAttempts(att.value.data?.data?.data ?? []);
    });
    return () => { cancelled = true; };
  }, [base, id]);

  const load = useCallback(async (boardId) => {
    setLoading(true);
    setError(null);
    try {
      const params = boardId ? { exam_board_id: boardId } : {};
      const [overviewRes, statsRes] = await Promise.all([
        api.get(`${base}/overview`),
        api.get(`${base}/stats`, { params }),
      ]);
      const overviewData = overviewRes.data?.data ?? {};
      setOverview(overviewData);
      setStats(statsRes.data?.data ?? null);

      const subjects = Array.isArray(overviewData.subject_wise_breakdown)
        ? overviewData.subject_wise_breakdown
        : [];
      const counts = await Promise.all(
        subjects.map((s) =>
          api
            .get(`${base}/subject`, {
              params: { subject_id: s.subject_id, include_areas: 0, ...(boardId ? { exam_board_id: boardId } : {}) },
            })
            .then((r) => [s.subject_id, r.data?.data?.subject_breakdown ?? null])
            .catch(() => [s.subject_id, null])
        )
      );
      setSubjectCounts(Object.fromEntries(counts));
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load this app user's progress.");
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(examBoardId); }, [load, examBoardId]);

  // Areas are the expensive query, so they are only fetched for the subject the admin opens.
  useEffect(() => {
    if (!areaSubjectId || areasBySubject[areaSubjectId]) return;
    api
      .get(`${base}/subject`, {
        params: { subject_id: areaSubjectId, include_areas: 1, ...(examBoardId ? { exam_board_id: examBoardId } : {}) },
      })
      .then((res) => {
        const d = res.data?.data ?? {};
        setAreasBySubject((prev) => ({
          ...prev,
          [areaSubjectId]: {
            weak_areas: d.weak_areas ?? [],
            medium_areas: d.medium_areas ?? [],
            best_areas: d.best_areas ?? [],
          },
        }));
      })
      .catch(() => {
        setAreasBySubject((prev) => ({
          ...prev,
          [areaSubjectId]: { weak_areas: [], medium_areas: [], best_areas: [] },
        }));
      });
  }, [areaSubjectId, areasBySubject, base, examBoardId]);

  useEffect(() => { setAreasBySubject({}); }, [examBoardId]);

  const subjectRows = useMemo(() => {
    const rows = Array.isArray(overview?.subject_wise_breakdown) ? overview.subject_wise_breakdown : [];
    const scoreById = new Map((stats?.avg_scores?.subjects ?? []).map((s) => [Number(s.subject_id), s]));
    const timeById = new Map((stats?.time_spent?.subjects ?? []).map((s) => [Number(s.subject_id), s]));
    return rows.map((row) => ({
      ...row,
      ...subjectPalette(row.subject_name),
      score: scoreById.get(Number(row.subject_id)) ?? null,
      time: timeById.get(Number(row.subject_id)) ?? null,
      counts: subjectCounts[row.subject_id] ?? null,
    }));
  }, [overview, stats, subjectCounts]);

  const totals = useMemo(() => ({
    worksheets: resourceProgress(sumResourceCounts(subjectRows, "worksheets")),
    mocks: resourceProgress(sumResourceCounts(subjectRows, "mock_exams")),
    papers: resourceProgress(sumResourceCounts(subjectRows, "papers")),
  }), [subjectRows]);

  const trendData = useMemo(() => {
    const days = trendPeriod === "week" ? 7 : 30;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));

    const buckets = new Map();
    for (let i = 0; i < days; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      buckets.set(dayKey(d), {
        name: trendPeriod === "week"
          ? d.toLocaleDateString('en-GB', { weekday: "short" })
          : String(d.getDate()),
        sum: 0,
        count: 0,
      });
    }

    activity.forEach((row) => {
      if (row.percentage == null || !row.completed_at) return;
      if (trendSubjectId && Number(row.subject_id) !== Number(trendSubjectId)) return;
      const bucket = buckets.get(dayKey(row.completed_at));
      if (!bucket) return;
      bucket.sum += Number(row.percentage);
      bucket.count += 1;
    });

    return Array.from(buckets.values()).map((b) => ({
      name: b.name,
      score: b.count > 0 ? Math.round(b.sum / b.count) : null,
    }));
  }, [activity, trendPeriod, trendSubjectId]);

  const filteredActivity = useMemo(() => (
    trendSubjectId
      ? activity.filter((row) => Number(row.subject_id) === Number(trendSubjectId))
      : activity
  ), [activity, trendSubjectId]);

  const areas = areaSubjectId ? areasBySubject[areaSubjectId] : null;
  const areaRows = areas?.[areaBucket] ?? [];
  const bucketMeta = AREA_BUCKETS.find((b) => b.key === areaBucket);
  const user = overview?.user ?? {};
  const readiness = overview?.readiness ?? null;
  const examBoards = overview?.exam_boards ?? [];

  if (loading && !overview) {
    return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/mobile-users/${id}`)} sx={{ mb: 2 }}>
        Back to user
      </Button>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#111827" }}>
            {user.full_name || "App user"} — Progress
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 600 }}>{user.email}</Typography>
        </Box>
        <Box>
          <Chip
            label="All boards"
            size="small"
            onClick={() => setExamBoardId(null)}
            sx={{ mr: 0.5, fontWeight: 700, bgcolor: examBoardId === null ? "#7c3aed" : "#f1f2f6", color: examBoardId === null ? "#fff" : "#374151" }}
          />
          {examBoards.map((board) => (
            <Chip
              key={board.id}
              label={board.code}
              size="small"
              onClick={() => setExamBoardId(board.id)}
              sx={{ mr: 0.5, mb: 0.5, fontWeight: 700, bgcolor: examBoardId === board.id ? "#7c3aed" : "#f1f2f6", color: examBoardId === board.id ? "#fff" : "#374151" }}
            />
          ))}
        </Box>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={CARD_SX}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }} mb={1}>Readiness</Typography>
            <ProgressRow
              label="Global readiness"
              value={readiness?.overall}
              right={`${pct(readiness?.overall)}%`}
              color="#7c3aed"
            />
            {(readiness?.schools ?? []).map((school) => (
              <ProgressRow
                key={school.school_id}
                label={school.school_name}
                value={school.average_score}
                right={`${pct(school.average_score)}%`}
                color="#667eea"
              />
            ))}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Typography variant="body2" sx={{ color: "#374151" }}>
                <strong>Overall completion:</strong> {pct(overview?.overall_completion?.completion_percentage)}%
                {" "}({overview?.overall_completion?.completed_items ?? 0}/{overview?.overall_completion?.total_items ?? 0})
              </Typography>
              <Typography variant="body2" sx={{ color: "#374151" }}>
                <strong>Avg score:</strong> {pct(stats?.avg_scores?.overall)}% · SAS {stats?.avg_scores?.overall_sas ?? "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#374151" }}>
                <strong>Time spent:</strong> {stats?.time_spent?.total || "—"}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={CARD_SX}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }} mb={1}>Resource completion</Typography>
            <ProgressRow label="Worksheets" value={totals.worksheets.percent} right={`${totals.worksheets.fraction} completed`} color="#673ab7" status={totals.worksheets.status} statusColor={totals.worksheets.statusColor} />
            <ProgressRow label="Mock exams" value={totals.mocks.percent} right={`${totals.mocks.fraction} completed`} color="#d32f2f" status={totals.mocks.status} statusColor={totals.mocks.statusColor} />
            <ProgressRow label="Past papers" value={totals.papers.percent} right={`${totals.papers.fraction} completed`} color="#1565c0" status={totals.papers.status} statusColor={totals.papers.statusColor} />
          </Paper>
        </Grid>

        {subjectRows.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">This app user has no subjects enabled yet.</Alert>
          </Grid>
        ) : subjectRows.map((subject) => {
          const worksheets = resourceProgress(subject.counts?.worksheets);
          const mocks = resourceProgress(subject.counts?.mock_exams);
          const papers = resourceProgress(subject.counts?.papers);
          return (
            <Grid item xs={12} md={6} lg={4} key={subject.subject_id}>
              <Paper sx={CARD_SX}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} gap={1}>
                  <Typography sx={{ fontWeight: 800, color: "#111827" }} noWrap>{subject.subject_name}</Typography>
                  {subject.score ? (
                    <Chip
                      size="small"
                      label={`${subject.score.average_score}% · SAS ${subject.score.sas_score}`}
                      sx={{ fontWeight: 700, fontSize: "0.68rem", bgcolor: subject.tint, color: subject.color }}
                    />
                  ) : null}
                </Stack>
                <ProgressRow label="Worksheets" value={worksheets.percent} right={worksheets.total != null ? `${worksheets.fraction}` : "—"} color="#673ab7" status={worksheets.status} statusColor={worksheets.statusColor} />
                <ProgressRow label="Mock exams" value={mocks.percent} right={mocks.total != null ? `${mocks.fraction}` : "—"} color="#d32f2f" status={mocks.status} statusColor={mocks.statusColor} />
                <ProgressRow label="Past papers" value={papers.percent} right={papers.total != null ? `${papers.fraction}` : "—"} color="#1565c0" status={papers.status} statusColor={papers.statusColor} />
                <ProgressRow label="Overall" value={subject.content_completion_percentage} right={`${pct(subject.content_completion_percentage)}%`} color={subject.color} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 700 }}>
                    {subject.time?.total_seconds > 0 ? subject.time.total : "No time logged"}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => { setAreaSubjectId(subject.subject_id); setAreaBucket("weak_areas"); }}
                    sx={{ textTransform: "none", fontWeight: 700, color: subject.color }}
                  >
                    Areas to improve
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          );
        })}

        {areaSubjectId ? (
          <Grid item xs={12}>
            <Paper sx={CARD_SX}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} gap={1} flexWrap="wrap">
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>
                  Areas to improve — {subjectRows.find((s) => s.subject_id === areaSubjectId)?.subject_name}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={areaBucket}
                  onChange={(_, next) => next && setAreaBucket(next)}
                >
                  {AREA_BUCKETS.map((bucket) => (
                    <ToggleButton key={bucket.key} value={bucket.key} sx={{ textTransform: "none", fontWeight: 700 }}>
                      {bucket.label} ({areas ? (areas[bucket.key] ?? []).length : "—"})
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              {!areas ? (
                <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>
              ) : areaRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 600, py: 2 }}>
                  Nothing in this band yet — the student needs more completed work in this subject.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Subtopic</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Topic</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Resources</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Average</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">SAS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {areaRows.map((row, idx) => (
                      <TableRow key={`${row.topic_id}-${row.subtopic_id ?? idx}`} hover>
                        <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{row.subtopic_name || row.topic_name}</TableCell>
                        <TableCell sx={{ color: "#6b7280" }}>{row.topic_name}</TableCell>
                        <TableCell align="right" sx={{ color: "#6b7280" }}>{row.attempts}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`${pct(row.avg_percentage)}%`}
                            sx={{ fontWeight: 800, bgcolor: bucketMeta?.bg, color: bucketMeta?.fg }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#6b7280" }}>{row.sas_score ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
        ) : null}

        <Grid item xs={12} lg={7}>
          <Paper sx={CARD_SX}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>Score trend</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label="All subjects"
                  size="small"
                  onClick={() => setTrendSubjectId(null)}
                  sx={{ fontWeight: 700, bgcolor: trendSubjectId === null ? "#7c3aed" : "#f1f2f6", color: trendSubjectId === null ? "#fff" : "#374151" }}
                />
                {subjectRows.map((subject) => (
                  <Chip
                    key={subject.subject_id}
                    label={subject.subject_name}
                    size="small"
                    onClick={() => setTrendSubjectId(subject.subject_id)}
                    sx={{ fontWeight: 700, bgcolor: trendSubjectId === subject.subject_id ? "#7c3aed" : "#f1f2f6", color: trendSubjectId === subject.subject_id ? "#fff" : "#374151" }}
                  />
                ))}
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={trendPeriod}
                  onChange={(_, next) => next && setTrendPeriod(next)}
                >
                  <ToggleButton value="week" sx={{ textTransform: "none", fontWeight: 700 }}>7d</ToggleButton>
                  <ToggleButton value="month" sx={{ textTransform: "none", fontWeight: 700 }}>30d</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <ChartTooltip formatter={(v) => (v == null ? "—" : `${v}%`)} />
                  <Line type="monotone" dataKey="score" stroke="#7c3aed" strokeWidth={2} connectNulls dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper sx={CARD_SX}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }} mb={1}>Recent activity</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Item</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Score</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredActivity.slice(0, 12).map((row, idx) => (
                  <TableRow key={`${row.type}-${row.resource_id}-${idx}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>{row.title || "—"}</Typography>
                      <Typography variant="caption" sx={{ color: "#9aa1ac" }}>
                        {row.type_label}{row.subject_name ? ` · ${row.subject_name}` : ""}{row.is_practice ? " · practice" : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>
                      {row.percentage == null ? "—" : `${pct(row.percentage)}%`}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(row.completed_at)}</TableCell>
                  </TableRow>
                ))}
                {filteredActivity.length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center">No activity yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={CARD_SX}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }} mb={1}>Worksheet attempts</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Worksheet</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Attempt</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Score</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Taken</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Answer sheet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attempts.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 700, color: "#111827" }}>
                      {row.test_name || `#${row.test_id}`}
                      {row.is_test_mode ? (
                        <Chip size="small" label="practice" sx={{ ml: 1, height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#f1f2f6", color: "#6b7280" }} />
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ color: "#6b7280" }}>
                      {[row.subject, row.strand, row.subtopic].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#6b7280" }}>{row.attempt_count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#374151" }}>
                      {pct(row.percentage)}% ({row.correct_answers}/{row.total_questions})
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 700 }}
                        onClick={() => navigate(`/admin/mobile-users/${id}/worksheet/${row.test_id}/attempt/${row.id}`)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {attempts.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">No worksheet attempts yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
